import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import expensesListApi from '../../../middleware/api/ExpenseManagement/ExpenseList/expensesListApi'
import {
  AuthorizedEmployee,
  CategoryListResponse,
  CountriesListResponse,
  CreditCardListResponse,
  EmpDepartmentListResponse,
  ExpenseCurrencyListResponse,
  ExpensesDetailsListResponse,
  ExpensesFormList,
  ExpensesListParams,
  ExpensesListResponse,
  InitialExpenseListSliceState,
  PaymentListResponse,
  ProjectsListResponse,
  SubCategoryListResponse,
  VendorListResponse,
} from '../../../types/ExpenseManagement/ExpenseList/expensesListTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'

const getEmpDepartmentsList = createAsyncThunk(
  '/assetManagement/getEmpDepartments',
  async (_, thunkApi) => {
    try {
      return await expensesListApi.getDepartmentsDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCategoriesList = createAsyncThunk(
  '/ExpenseManagement/getCategoryList',
  async (_, thunkApi) => {
    try {
      return await expensesListApi.getCategoryDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCountriesList = createAsyncThunk(
  '/ExpenseManagement/getCountries',
  async (_, thunkApi) => {
    try {
      return await expensesListApi.getCountryDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getPaymentsList = createAsyncThunk(
  '/ExpenseManagement/getPaymentList',
  async (_, thunkApi) => {
    try {
      return await expensesListApi.getPaymentDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getExpensesList = createAsyncThunk(
  'ExpenseManagement/getExpensesList',
  async (props: ExpensesListParams, thunkApi) => {
    try {
      return await expensesListApi.getExpensesList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getSelectedSubCategory = createAsyncThunk(
  'ExpenseManagement/getSelectedSubCategories',
  async (categoryId: number[], thunkApi) => {
    try {
      return await expensesListApi.getSelectedSubCategories(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateExpenseDetails = createAsyncThunk(
  '/ExpenseManagement/updateExpenses',
  async (data: ExpensesFormList, thunkApi) => {
    try {
      return await expensesListApi.updateExpenseDetails(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteExpensesList = createAsyncThunk(
  '/ExpenseManagement/deleteExpense',
  async (formId: number, thunkApi) => {
    try {
      return await expensesListApi.deletedExpenseRecord(formId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addCopiedExpensesList = createAsyncThunk(
  'ExpenseManagement/addCopiedExpensesList',
  async (formIds: number[], thunkApi) => {
    try {
      return await expensesListApi.addCopiedExpensesList(formIds)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getEmployeesList = createAsyncThunk(
  '/jobapplicant/getAllProfileEmployeesData',
  async (searchStr: string, thunkApi) => {
    try {
      return await expensesListApi.getAllEmployeesList(searchStr)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getProjectsList = createAsyncThunk(
  '/ExpenseManagement/getMatchedProjects',
  async (projectName: string, thunkApi) => {
    try {
      return await expensesListApi.projectsList(projectName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getVendorsList = createAsyncThunk(
  '/ExpenseManagement/getVendorList',
  async (vendorName: string, thunkApi) => {
    try {
      return await expensesListApi.vendorsList(vendorName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCreditCardsDetails = createAsyncThunk(
  '/ExpenseManagement/getCardsList',
  async (_, thunkApi) => {
    try {
      return await expensesListApi.getCreditCardsList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCurrenciesList = createAsyncThunk(
  '/ExpenseManagement/getCurrencyList',
  async (_, thunkApi) => {
    try {
      return await expensesListApi.getExpensesCurrencyList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getExpensesDetails = createAsyncThunk(
  '/ExpenseManagement/getExpenseDetails',
  async (formId: string, thunkApi) => {
    try {
      return await expensesListApi.getExpenseDetailsList(formId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const editCategoriesList = createAsyncThunk(
  '/ExpenseManagement/editCategory',
  async (categoryId: number, thunkApi) => {
    try {
      return await expensesListApi.editCategories(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getSubCategoryList = createAsyncThunk(
  '/ExpenseManagement/getSubCategories',
  async (categoryId: number, thunkApi) => {
    try {
      return await expensesListApi.getSubCategories(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateExpenses = createAsyncThunk(
  '/ExpenseManagement/updateExpenses',
  async (data: ExpensesDetailsListResponse, thunkApi) => {
    try {
      return await expensesListApi.updateExpenses(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const curMonth = 'Current Month'

export const initialExpenseListState: InitialExpenseListSliceState = {
  isLoading: ApiLoadingState.idle,
  isCategoryLoading: ApiLoadingState.idle,
  error: null,
  categoriesList: [],
  empDepartments: [],
  paymentList: [],
  countriesList: [],
  expensesListData: [],
  subCategoryList: [],
  getExpensesList: {} as ExpensesListResponse,
  expensesListSize: 0,
  expensesTotalAmount: '',
  employeesList: [],
  projectsList: [],
  vendorsList: [],
  creditCardsList: [],
  currenciesList: [],
  expensesDetailsList: [],
  getSubcategoryList: [],
  editExpensesDetails: {} as ExpensesFormList,
  customFromDateValue: '',
  customToDateValue: '',
  departmentName: '',
  countryName: '',
  selectDate: curMonth,
  modeOfPayment: '',
  searchValue: '',

  selectedCategoriesList: [],
  selectedSubCategoriesList: [],
  isLoadingEdit: ApiLoadingState.idle,
  isEdit: false,
}

const expensesListSlice = createSlice({
  name: 'expenseList',
  initialState: initialExpenseListState,
  reducers: {
    setCustomFromValue: (state, action) => {
      state.customFromDateValue = action.payload
    },
    setCustomToValue: (state, action) => {
      state.customToDateValue = action.payload
    },
    setDepartmentValue: (state, action) => {
      state.departmentName = action.payload
    },
    setSelectDateValue: (state, action) => {
      state.selectDate = action.payload
    },
    setCountryNameValue: (state, action) => {
      state.countryName = action.payload
    },
    setModeOfPaymentValue: (state, action) => {
      state.modeOfPayment = action.payload
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload
    },
    setSelectedCategories: (state, action) => {
      state.selectedCategoriesList = action.payload
    },
    setSelectedSubCategories: (state, action) => {
      state.selectedSubCategoriesList = action.payload
    },
    setIsEdit: (state, action) => {
      state.isEdit = action.payload
    },
  },
  // commented out several unnecessary isLoading states
  // that is affecting the loading behavior of expenseList
  extraReducers: (builder) => {
    builder
      .addCase(getCategoriesList.fulfilled, (state, action) => {
        state.isCategoryLoading = ApiLoadingState.succeeded
        state.categoriesList = action.payload
      })
      .addCase(getEmpDepartmentsList.fulfilled, (state, action) => {
        // state.isLoading = ApiLoadingState.succeeded
        state.empDepartments = action.payload
      })
      .addCase(getPaymentsList.fulfilled, (state, action) => {
        // state.isLoading = ApiLoadingState.succeeded
        state.paymentList = action.payload
      })
      .addCase(getCountriesList.fulfilled, (state, action) => {
        // state.isLoading = ApiLoadingState.succeeded
        state.countriesList = action.payload
      })
      .addCase(getSelectedSubCategory.fulfilled, (state, action) => {
        state.isCategoryLoading = ApiLoadingState.succeeded
        state.subCategoryList = action.payload
      })
      .addCase(getEmployeesList.fulfilled, (state, action) => {
        // state.isLoading = ApiLoadingState.succeeded
        state.employeesList = action.payload
      })
      .addCase(getProjectsList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.projectsList = action.payload
      })
      .addCase(getVendorsList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.vendorsList = action.payload
      })
      .addCase(getCreditCardsDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.creditCardsList = action.payload
      })
      .addCase(getCurrenciesList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.currenciesList = action.payload
      })
      .addCase(getExpensesDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.editExpensesDetails = action.payload
      })
      .addCase(editCategoriesList.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getSubCategoryList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.subCategoryList = action.payload
      })
      .addCase(getExpensesList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.expensesListSize = action.payload.size
        state.expensesListData = action.payload.list
        state.expensesTotalAmount = action.payload.totalExpenseAmount
      })
      .addCase(updateExpenseDetails.fulfilled, (state) => {
        state.isLoadingEdit = ApiLoadingState.succeeded
      })
      .addCase(updateExpenseDetails.pending, (state) => {
        state.isLoadingEdit = ApiLoadingState.loading
      })
      .addCase(updateExpenseDetails.rejected, (state) => {
        state.isLoadingEdit = ApiLoadingState.failed
      })
      .addMatcher(
        isAnyOf(
          updateExpenseDetails.fulfilled,
          deleteExpensesList.fulfilled,
          addCopiedExpensesList.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          getExpensesDetails.fulfilled,
          editCategoriesList.fulfilled,
          updateExpenses.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          // getCategoriesList.pending,
          // getEmpDepartmentsList.pending,
          // getCountriesList.pending,
          // getPaymentsList.pending,
          getExpensesList.pending,
          updateExpenseDetails.pending,
          deleteExpensesList.pending,
          addCopiedExpensesList.pending,
          getEmployeesList.pending,
          getProjectsList.pending,
          getVendorsList.pending,
          getCreditCardsDetails.pending,
          getCurrenciesList.pending,
          getExpensesDetails.pending,
          editCategoriesList.pending,
          // getSubCategoryList.pending,
          updateExpenses.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoriesList.pending,
          getSelectedSubCategory.pending,
          getSubCategoryList.pending,
        ),
        (state) => {
          state.isCategoryLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          // getCategoriesList.rejected,
          // getEmpDepartmentsList.rejected,
          // getCountriesList.rejected,
          // getPaymentsList.rejected,
          // getSelectedSubCategory.rejected,
          getExpensesList.rejected,
          updateExpenseDetails.rejected,
          deleteExpensesList.rejected,
          addCopiedExpensesList.rejected,
          getEmployeesList.rejected,
          getProjectsList.rejected,
          getVendorsList.rejected,
          getCreditCardsDetails.rejected,
          getCurrenciesList.rejected,
          getExpensesDetails.rejected,
          editCategoriesList.rejected,
          // getSubCategoryList.rejected,
          updateExpenses.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
      .addMatcher(
        isAnyOf(
          getCategoriesList.rejected,
          getSelectedSubCategory.rejected,
          getSubCategoryList.rejected,
        ),
        (state) => {
          state.isCategoryLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.expensesList.isLoading

const getIsLoadingEdit = (state: RootState): LoadingState =>
  state.expensesList.isLoadingEdit

const isCategoryLoading = (state: RootState): LoadingState =>
  state.expensesList.isCategoryLoading

const categoryList = (state: RootState): CategoryListResponse[] =>
  state.expensesList.categoriesList

const subCategoryList = (state: RootState): SubCategoryListResponse[] =>
  state.expensesList.subCategoryList

const departmentList = (state: RootState): EmpDepartmentListResponse[] =>
  state.expensesList.empDepartments

const paymentsList = (state: RootState): PaymentListResponse[] =>
  state.expensesList.paymentList

const countriesList = (state: RootState): CountriesListResponse[] =>
  state.expensesList.countriesList

const getExpenses = (state: RootState): ExpensesListResponse =>
  state.expensesList.getExpensesList

const expensesList = (state: RootState): ExpensesFormList[] =>
  state.expensesList.expensesListData

const expensesListSize = (state: RootState): number =>
  state.expensesList.expensesListSize

const expensesAmount = (state: RootState): string =>
  state.expensesList.expensesTotalAmount

const currenciesList = (state: RootState): ExpenseCurrencyListResponse[] =>
  state.expensesList.currenciesList

const employeesList = (state: RootState): AuthorizedEmployee[] =>
  state.expensesList.employeesList

const allProjectsList = (state: RootState): ProjectsListResponse[] =>
  state.expensesList.projectsList

const allVendorsList = (state: RootState): VendorListResponse[] =>
  state.expensesList.vendorsList

const creditCards = (state: RootState): CreditCardListResponse[] =>
  state.expensesList.creditCardsList

const editExpenses = (state: RootState): ExpensesFormList =>
  state.expensesList.editExpensesDetails

const getCustomFromDateValue = (state: RootState): string | Date =>
  state.expensesList.customFromDateValue

const getCustomToDateValue = (state: RootState): string | Date =>
  state.expensesList.customToDateValue

const departmentNameValue = (state: RootState): string =>
  state.expensesList.departmentName

const selectDateValue = (state: RootState): string =>
  state.expensesList.selectDate

const countryNameValue = (state: RootState): string =>
  state.expensesList.countryName

const modeOfPaymentValue = (state: RootState): string =>
  state.expensesList.modeOfPayment

const getSearchValue = (state: RootState): string =>
  state.expensesList.searchValue

const getSelectedCategories = (state: RootState): CategoryListResponse[] =>
  state.expensesList.selectedCategoriesList
const getSelectedSubCategories = (state: RootState): CategoryListResponse[] =>
  state.expensesList.selectedSubCategoriesList
const getIsEdit = (state: RootState): boolean => state.expensesList.isEdit

const expensesListThunk = {
  getCategoriesList,
  getEmpDepartmentsList,
  getCountriesList,
  getPaymentsList,
  getExpensesList,
  getSelectedSubCategory,
  updateExpenseDetails,
  deleteExpensesList,
  addCopiedExpensesList,
  getEmployeesList,
  getExpensesDetails,
  getProjectsList,
  getVendorsList,
  getSubCategoryList,
  editCategoriesList,
  updateExpenses,
  getCreditCardsDetails,
  getCurrenciesList,
}

const expensesFormSelectors = {
  isLoading,
  isCategoryLoading,
  categoryList,
  subCategoryList,
  departmentList,
  paymentsList,
  countriesList,
  expensesList,
  expensesListSize,
  getExpenses,
  expensesAmount,
  currenciesList,
  employeesList,
  allProjectsList,
  allVendorsList,
  creditCards,
  editExpenses,
  getCustomFromDateValue,
  getCustomToDateValue,
  departmentNameValue,
  selectDateValue,
  countryNameValue,
  modeOfPaymentValue,
  getSearchValue,
  getSelectedCategories,
  getSelectedSubCategories,
  getIsLoadingEdit,
  getIsEdit,
}

export const expensesListService = {
  ...expensesListThunk,
  actions: expensesListSlice.actions,
  selectors: expensesFormSelectors,
}

export default expensesListSlice.reducer
